import axios from 'axios'
import { endpoints } from './endpoints.js'
import {
  getAccessToken, setAccessToken,
  getRefreshToken, setRefreshToken, clearTokens,
} from '../utils/storage.js'

// A single axios instance shared by every api/*.js module. Has:
//   - JWT Authorization header on every request
//   - Automatic access-token refresh on 401 using the stored refresh token
//   - Longer default timeout to tolerate slow PNG connections
//   - Outgoing X-Request-ID on every call; echoed id captured on response +
//     attached to errors so the UI can show a support reference.
export const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '',
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
})

// ---------------------------------------------------------------------------
// Request id helpers
// ---------------------------------------------------------------------------
// A short (32 hex chars) id we generate per outgoing request. The server's
// audit.AuditContextMiddleware reuses it if present, so the same id threads
// through: axios.config.headers -> server log records -> audit rows ->
// response X-Request-ID header -> this client's `response.requestId` + any
// error's `error.requestId`. Support staff asked for a single string that
// lets them reconstruct the whole request.
function newRequestId() {
  // crypto.randomUUID is available in all browsers we target; strip hyphens
  // to match the backend's hex format (audit rows store it as 32 chars).
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID().replace(/-/g, '')
    }
  } catch { /* fall through */ }
  // Last-ditch fallback: Math.random-based hex. Not cryptographically strong
  // but that's fine — server-side audit rows are the source of truth.
  let out = ''
  for (let i = 0; i < 32; i += 1) {
    out += Math.floor(Math.random() * 16).toString(16)
  }
  return out
}

function readResponseRequestId(res) {
  // Axios normalises response headers to lower-case, but defensively check
  // both cases for robustness across adapters.
  if (!res) return null
  const h = res.headers || {}
  return h['x-request-id'] || h['X-Request-ID'] || null
}

api.interceptors.request.use((config) => {
  const token = getAccessToken()
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`
  }
  // Attach X-Request-ID. Respect a caller-provided override so e.g. retries
  // re-use the original id, making support tickets easier to follow.
  if (!config.headers['X-Request-ID'] && !config.headers['x-request-id']) {
    config.headers['X-Request-ID'] = newRequestId()
  }
  return config
})

api.interceptors.response.use(
  (r) => {
    // Expose the echoed id on the response object for any caller that wants
    // to log it alongside a success (e.g. "created encounter #42 rid=abcd").
    r.requestId = readResponseRequestId(r) || r.config?.headers?.['X-Request-ID'] || null
    return r
  },
  (error) => {
    // Attach requestId to the error even when the request never reached a
    // response (timeout, network error) — the outbound header id is still
    // meaningful for client-side log correlation.
    const outbound = error?.config?.headers?.['X-Request-ID']
      || error?.config?.headers?.['x-request-id']
      || null
    const echoed = readResponseRequestId(error?.response)
    error.requestId = echoed || outbound

    // eslint-disable-next-line no-console
    console.error(
      '[api]',
      error?.config?.method?.toUpperCase(),
      error?.config?.url,
      `rid=${error.requestId || 'n/a'}`,
      error?.response?.status ?? '(no response)',
      error?.message,
    )
    return Promise.reject(error)
  }
)

// Refresh token coordination: if several requests 401 at once we only fire
// one refresh call and share its promise.
let refreshPromise = null

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config
    const status = error.response?.status
    const isRefreshCall = original?.url?.includes(endpoints.refresh)

    if (status === 401 && !original._retry && !isRefreshCall && getRefreshToken()) {
      original._retry = true
      try {
        if (!refreshPromise) {
          refreshPromise = axios
            .post((import.meta.env.VITE_API_BASE_URL || '') + endpoints.refresh,
              { refresh: getRefreshToken() },
              { headers: { 'Content-Type': 'application/json' } })
            .then((res) => {
              const access = res.data?.access
              if (!access) throw new Error('No access token in refresh response')
              setAccessToken(access)
              // ROTATE_REFRESH_TOKENS is on server-side: each refresh returns a
              // NEW refresh token and BLACKLISTS the old one. In body mode the
              // new token arrives in the response body and MUST be persisted, or
              // the next refresh presents a blacklisted token and the user is
              // logged out mid-session. (Cookie mode handles this server-side.)
              const rotated = res.data?.refresh
              if (rotated) setRefreshToken(rotated)
              return access
            })
            .finally(() => { refreshPromise = null })
        }
        const newToken = await refreshPromise
        original.headers = { ...(original.headers || {}), Authorization: `Bearer ${newToken}` }
        return api(original)
      } catch (e) {
        clearTokens()
        if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
          window.location.href = '/login'
        }
        return Promise.reject(e)
      }
    }
    return Promise.reject(error)
  }
)
